/**
 * Device TF storage panel (CAM-FW-NB-4 · Waveshare P4+C6 POE + TF).
 * Shown on #/devices/:id when board supports TF spool.
 */

/** @param {object} d device row — auto: firmware tf_storage.supported or board_profile */
export function deviceTfStorageSupported(d) {
  const tf = d && d.last_status_json && d.last_status_json.tf_storage;
  if (tf && tf.supported === false) return false;
  if (tf && tf.supported === true) return true;
  const board = String((d && d.board_profile) || "").trim().toLowerCase();
  if (board === "waveshare_esp32_p4_c6_poe_eth") return true;
  return false;
}

/** @param {number} n */
export function formatTfBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "—";
  if (v < 1024) return `${Math.round(v)} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(2)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** @param {object} tf from last_status_json.tf_storage */
export function renderTfStorageSummaryHtml(tf, escapeHtml) {
  const esc = escapeHtml || ((s) => String(s));
  if (!tf || !tf.supported) {
    return `<p class="muted" style="margin:0">TF storage not supported on this board.</p>`;
  }
  if (!tf.present) {
    return `<p class="muted" style="margin:0">No TF card detected — camera snapshots use live pipe upload. Insert a card to enable local spool.</p>`;
  }
  const total = Number(tf.total_bytes || 0);
  const used = Number(tf.used_bytes || 0);
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const spoolFiles = Number(tf.spool_files || 0);
  const spoolBytes = Number(tf.spool_bytes || 0);
  const exportActive = !!tf.export_active;
  return `<div class="device-tf-storage">
    <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:6px">
      <span class="badge online">TF card</span>
      ${exportActive ? `<span class="chip">Exporting…</span>` : ""}
    </div>
    <div class="muted" style="font-size:12px;margin-bottom:4px">${esc(formatTfBytes(used))} used · ${esc(formatTfBytes(total))} total · ${esc(formatTfBytes(Math.max(0, total - used)))} free</div>
    <progress id="devTfUsageBar" value="${pct}" max="100" style="width:100%;height:12px;margin:0 0 8px">${pct}%</progress>
    <div class="muted" style="font-size:12px">Spool queue: <span class="mono" id="devTfSpoolStat">${esc(String(spoolFiles))} file(s) · ${esc(formatTfBytes(spoolBytes))}</span></div>
  </div>`;
}

/**
 * @param {{ canOperate: boolean }} opts
 */
export function deviceTfStoragePanelHtml(opts) {
  const canOp = !!(opts && opts.canOperate);
  return `<details class="card device-drawer" id="devTfStorageCard">
    <summary class="device-drawer__summary">
      <span class="device-drawer__title">TF card</span>
      <span class="device-drawer__hint muted">Waveshare · spool · export</span>
    </summary>
    <div class="device-drawer__body">
      <p class="muted" style="margin:0 0 10px">MicroSD spool on supported boards (auto-detected). Other boards skip this panel. Usage updates from live device status.</p>
      <div id="devTfStorageBody"><p class="muted" style="margin:0">Loading…</p></div>
      <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:12px">
        <button type="button" class="btn secondary sm btn-tap" id="devTfRefresh" ${canOp ? "" : "disabled"} title="Send get_info">Refresh</button>
        <button type="button" class="btn secondary sm btn-tap" id="devTfExport" ${canOp ? "" : "disabled"} title="Upload pending spool JPEGs to cloud">Upload pending</button>
        <button type="button" class="btn danger sm btn-tap" id="devTfFormat" ${canOp ? "" : "disabled"} title="Erase all files on TF card">Format card</button>
      </div>
      <p class="muted" id="devTfActionMsg" style="margin:8px 0 0;font-size:12px;min-height:1.2em"></p>
    </div>
  </details>`;
}

/**
 * @param {HTMLElement} view
 * @param {{ deviceId: string, getDevice: () => object, canOperate: boolean, api: Function, toast: Function, escapeHtml: Function, buildPostDeviceCommandsPath: (id: string) => string, isRouteCurrent: (n: number) => boolean, routeSeq: number, timeoutMs?: number }} ctx
 */
export function wireDeviceTfStoragePanel(view, ctx) {
  const {
    deviceId,
    getDevice,
    canOperate,
    api,
    toast,
    escapeHtml,
    buildPostDeviceCommandsPath,
    isRouteCurrent,
    routeSeq,
    timeoutMs = 45000,
  } = ctx;

  const bodyEl = view.querySelector("#devTfStorageBody");
  const msgEl = view.querySelector("#devTfActionMsg");
  const cardEl = view.querySelector("#devTfStorageCard");

  const paint = (dev) => {
    if (!bodyEl) return;
    if (!deviceTfStorageSupported(dev)) {
      if (cardEl) cardEl.style.display = "none";
      return;
    }
    if (cardEl) cardEl.style.display = "";
    const tf = (dev.last_status_json && dev.last_status_json.tf_storage) || {};
    bodyEl.innerHTML = renderTfStorageSummaryHtml(tf, escapeHtml);
  };

  const setMsg = (txt, kind) => {
    if (!msgEl) return;
    msgEl.textContent = String(txt || "");
    msgEl.className = kind === "err" ? "badge offline" : kind === "ok" ? "badge online" : "muted";
    msgEl.style.fontSize = "12px";
  };

  const sendCmd = async (cmd, params) => {
    if (!canOperate) {
      toast("Operate permission required", "err");
      return;
    }
    setMsg("Sending…", "");
    try {
      await api(buildPostDeviceCommandsPath(deviceId), {
        method: "POST",
        body: { cmd, params: params || {} },
        timeoutMs,
      });
      setMsg(`Command ${cmd} queued`, "ok");
      toast(`${cmd} sent`, "ok");
    } catch (e) {
      setMsg((e && e.message) || "Command failed", "err");
      toast((e && e.message) || "Command failed", "err");
    }
  };

  paint(getDevice());

  const refreshBtn = view.querySelector("#devTfRefresh");
  if (refreshBtn) {
    refreshBtn.addEventListener("click", () => sendCmd("get_info", {}));
  }

  const exportBtn = view.querySelector("#devTfExport");
  if (exportBtn) {
    exportBtn.addEventListener("click", async () => {
      if (!confirm("Upload all pending spool JPEGs from TF to the server?")) return;
      await sendCmd("tf_export", {});
    });
  }

  const formatBtn = view.querySelector("#devTfFormat");
  if (formatBtn) {
    formatBtn.addEventListener("click", async () => {
      if (
        !confirm(
          "Format TF card? ALL files on the card will be erased. This cannot be undone.",
        )
      ) {
        return;
      }
      await sendCmd("tf_format", { confirm: true });
    });
  }

  return {
    refresh(dev) {
      if (!isRouteCurrent(routeSeq)) return;
      paint(dev || getDevice());
    },
  };
}
